import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ChevronLeft,
  ChevronRight,
  Check,
  Banknote,
  CreditCard,
  Gift,
  Receipt,
  User,
  Calculator,
  BarChart3,
  Percent,
  Minus,
  Edit2,
  X,
  ShoppingBag,
  Plus,
  Search,
  Package,
  ScanLine,
  Camera,
  Scissors,
  AlertCircle,
  Trash2,
  Wallet,
  Star,
  CheckCircle2
} from "lucide-react";
import ReportOperatori from "@/components/ReportOperatori";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import BarcodeScanner from "@/components/BarcodeScanner";
import { Label } from "@/components/ui/label";
import ServiziSelector from "@/components/agenda/ServiziSelector";
import CassaServiziDialog from "@/components/agenda/CassaServiziDialog";
import IncassoCompletatoDialog from "@/components/agenda/IncassoCompletatoDialog";

export default function Cassa() {
  const { token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [appuntamenti, setAppuntamenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [mostraCassa, setMostraCassa] = useState(false);
  const [clienteSelezionato, setClienteSelezionato] = useState(null);
  const [incassoCompletato, setIncassoCompletato] = useState(null);
  const [cardSaldo, setCardSaldo] = useState(null);
  const [loadingCard, setLoadingCard] = useState(false);
  const [corrispettivi, setCorrispettivi] = useState(null);
  const [showCorrispettivi, setShowCorrispettivi] = useState(false);
  const [showReportOperatori, setShowReportOperatori] = useState(false);
  const [operatoreSelezionato, setOperatoreSelezionato] = useState("");
  const [operatori, setOperatori] = useState([]);
  
  // Stato per vendita libera (cliente cercato senza appuntamento)
  const [showRicercaCliente, setShowRicercaCliente] = useState(false);
  const [searchCliente, setSearchCliente] = useState("");
  const [clientiTutti, setClientiTutti] = useState([]);
  const [loadingClienti, setLoadingClienti] = useState(false);
  
  // Stato per creazione rapida cliente
  const [showCreaCliente, setShowCreaCliente] = useState(false);
  const [nuovoCliente, setNuovoCliente] = useState({
    nome: "", cognome: "", telefono: "", genere: "", is_starred: true
  });
  const [creatingCliente, setCreatingCliente] = useState(false);

  // Reminder anagrafica incompleta prima del pagamento (telefono + data_nascita)
  const [anagraficaAlertOpen, setAnagraficaAlertOpen] = useState(false);
  const [anagraficaCampiMancanti, setAnagraficaCampiMancanti] = useState([]);
  const [anagraficaCliente, setAnagraficaCliente] = useState(null);
  const [pendingPaymentMetodo, setPendingPaymentMetodo] = useState(null);
  const [quickEditAnagrafica, setQuickEditAnagrafica] = useState({ telefono: "", data_nascita: "" });
  const [savingAnagrafica, setSavingAnagrafica] = useState(false);

  // Stato per modifica prezzi e sconti
  const [prezziModificati, setPrezziModificati] = useState({});
  const [serviziRimossi, setServiziRimossi] = useState(new Set()); // idx dei servizi originali eliminati
  // Anteprima scontrino prima del pagamento
  const [showAnteprimaScontrino, setShowAnteprimaScontrino] = useState(false);
  const [metodoPendente, setMetodoPendente] = useState(null);
  // Ricarica rapida Card (alert saldo basso)
  const [showRicaricaRapida, setShowRicaricaRapida] = useState(false);
  const [ricaricaImporto, setRicaricaImporto] = useState(50);
  const [ricaricaMetodo, setRicaricaMetodo] = useState("contanti");
  const [savingRicarica, setSavingRicarica] = useState(false);
  const [scontoTipo, setScontoTipo] = useState("nessuno"); // "nessuno", "percentuale", "importo"
  const [scontoValore, setScontoValore] = useState("");
  const [editingServizio, setEditingServizio] = useState(null);
  
  // Stato per vendita prodotti
  const [prodottiDisponibili, setProdottiDisponibili] = useState([]);
  const [prodottiSelezionati, setProdottiSelezionati] = useState([]);
  const [showProdotti, setShowProdotti] = useState(false);
  const [searchProdotto, setSearchProdotto] = useState("");
  const [editingProdotto, setEditingProdotto] = useState(null);
  
  // Stato per servizi aggiuntivi
  const [serviziDisponibili, setServiziDisponibili] = useState([]);
  const [serviziAggiunti, setServiziAggiunti] = useState([]);
  const [showServizi, setShowServizi] = useState(false);
  const [searchServizio, setSearchServizio] = useState("");
  
  // Stato per barcode scanner
  const [showScanner, setShowScanner] = useState(false);
  const [scanning, setScanning] = useState(false);

  // Gestisci appuntamento passato dall'Agenda
  useEffect(() => {
    if (location.state?.appuntamento && location.state?.fromAgenda) {
      const app = location.state.appuntamento;
      // Imposta la data dell'appuntamento
      if (app.data) {
        setSelectedDate(new Date(app.data));
      }
      // Apri direttamente la cassa con l'appuntamento selezionato
      setTimeout(() => {
        openCassa(app);
      }, 500);
      // Pulisci lo state per evitare riaperture
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    fetchAppuntamenti();
    fetchCorrispettivi();
    fetchOperatori();
    fetchProdottiRivendita();
    fetchServizi();
  }, [selectedDate]);
  
  const fetchProdottiRivendita = async () => {
    try {
      const response = await axios.get(`${API}/prodotti`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filtra solo prodotti rivendita con quantità > 0
      const rivendita = response.data.filter(p => p.tipo_prodotto === 'rivendita' && p.quantita > 0);
      setProdottiDisponibili(rivendita);
    } catch (error) {
      console.error("Errore caricamento prodotti:", error);
    }
  };
  
  const fetchServizi = async () => {
    try {
      const response = await axios.get(`${API}/servizi`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServiziDisponibili(response.data);
    } catch (error) {
      console.error("Errore caricamento servizi:", error);
    }
  };

  const fetchOperatori = async () => {
    try {
      const response = await axios.get(`${API}/operatori`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOperatori(response.data);
    } catch (error) {
      console.error("Errore caricamento operatori:", error);
    }
  };

  const fetchCorrispettivi = async () => {
    const dataStr = format(selectedDate, "yyyy-MM-dd");
    try {
      const response = await axios.get(`${API}/cassa/corrispettivi?data=${dataStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCorrispettivi(response.data);
    } catch (error) {
      console.error("Errore caricamento corrispettivi:", error);
    }
  };

  const fetchAppuntamenti = async () => {
    setLoading(true);
    const dataStr = format(selectedDate, "yyyy-MM-dd");
    try {
      const response = await axios.get(`${API}/appuntamenti?data=${dataStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppuntamenti(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  // Verifica anagrafica cliente prima del pagamento.
  // Se mancano telefono o data_nascita → apre dialog "Completa ora o Ignora".
  const handlePagamentoClick = async (metodo) => {
    if (!clienteSelezionato) return;
    if (!operatoreSelezionato) {
      toast.error("Seleziona un operatore prima di confermare il pagamento");
      return;
    }
    // Vendita libera senza cliente_id → procedi direttamente
    if (!clienteSelezionato.cliente_id) {
      return confermaPagamento(metodo);
    }
    try {
      const res = await axios.get(`${API}/clienti/${clienteSelezionato.cliente_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const cl = res.data;
      const mancanti = [];
      if (!cl.telefono || !String(cl.telefono).trim()) mancanti.push("telefono");
      if (!cl.data_nascita || !String(cl.data_nascita).trim()) mancanti.push("data nascita");
      
      if (mancanti.length > 0) {
        setAnagraficaCliente(cl);
        setAnagraficaCampiMancanti(mancanti);
        setQuickEditAnagrafica({
          telefono: cl.telefono || "",
          data_nascita: cl.data_nascita || ""
        });
        setPendingPaymentMetodo(metodo);
        setAnagraficaAlertOpen(true);
        return;
      }
    } catch {
      // Se il fetch fallisce, non bloccare il pagamento
    }
    return confermaPagamento(metodo);
  };

  // Salva i dati mancanti dell'anagrafica e poi prosegue col pagamento
  const handleSalvaAnagraficaEPaga = async () => {
    if (!anagraficaCliente) return;
    setSavingAnagrafica(true);
    try {
      await axios.put(
        `${API}/clienti/${anagraficaCliente.id}`,
        { ...anagraficaCliente, ...quickEditAnagrafica },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Anagrafica aggiornata");
      setAnagraficaAlertOpen(false);
      const metodo = pendingPaymentMetodo;
      setPendingPaymentMetodo(null);
      if (metodo) await confermaPagamento(metodo);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore aggiornamento anagrafica");
    } finally {
      setSavingAnagrafica(false);
    }
  };

  // Ignora e procedi col pagamento senza completare l'anagrafica
  const handleIgnoraEPaga = async () => {
    setAnagraficaAlertOpen(false);
    const metodo = pendingPaymentMetodo;
    setPendingPaymentMetodo(null);
    if (metodo) await confermaPagamento(metodo);
  };

  const confermaPagamento = async (metodo) => {
    if (!clienteSelezionato) return;
    
    // OPERATORE OBBLIGATORIO
    if (!operatoreSelezionato) {
      toast.error("Seleziona un operatore prima di confermare il pagamento");
      return;
    }
    
    const totaleFinale = calcolaTotaleFinale();
    const subtotaleServizi = getSubtotaleServizi();
    const subtotaleProdotti = getSubtotaleProdotti();
    
    // Calcola sconto applicato sul totale
    const subtotaleTotale = subtotaleServizi + subtotaleProdotti;
    const scontoApplicato = subtotaleTotale - totaleFinale;
    
    try {
      // Prepara descrizione con servizi (originali attivi + aggiunti) e prodotti
      const originaliAttivi = (clienteSelezionato.servizi || [])
        .map((s, idx) => ({ s, idx }))
        .filter(({ idx }) => !serviziRimossi.has(idx));

      const tuttiServizi = [
        ...originaliAttivi.map(({ s }) => s.nome),
        ...serviziAggiunti.map(s => s.nome)
      ];
      const descrizioneServizi = tuttiServizi.join(' + ');
      const descrizioneProdotti = prodottiSelezionati.map(p => `${p.nome} x${p.quantita}`).join(', ');
      const descrizioneCompleta = [descrizioneServizi, descrizioneProdotti].filter(Boolean).join(' | ');

      // Helper: ottiene il prezzo modificato (gestisce correttamente lo 0 che è valore valido!)
      const getPrezzoModificato = (idx, fallback) => {
        const raw = prezziModificati[idx];
        if (raw === undefined || raw === null || raw === '') {
          return parseFloat(fallback) || 0;
        }
        const num = parseFloat(raw);
        return isNaN(num) ? (parseFloat(fallback) || 0) : num;
      };

      // Dettaglio servizi con prezzi REALI incassati (per analisi accurate), esclusi i rimossi
      const serviziOriginaliFinali = originaliAttivi.map(({ s, idx }) => ({
        nome: s.nome,
        prezzo: getPrezzoModificato(idx, s.prezzo),
        durata_minuti: s.durata_minuti,
        categoria: s.categoria || null
      }));
      const serviziAggiuntiFinali = serviziAggiunti.map((s) => ({
        nome: s.nome,
        prezzo: getPrezzoModificato(s.idx, s.prezzo),
        durata_minuti: s.durata_minuti,
        categoria: s.categoria || null
      }));
      const serviziFinali = [...serviziOriginaliFinali, ...serviziAggiuntiFinali];
      const serviziDettaglioTx = serviziFinali.map(s => ({
        nome: s.nome,
        prezzo_pagato: s.prezzo,
        categoria: s.categoria
      }));
      
      // Se paga con Card Prepagata, usa l'API specifica (che ora crea anche la transazione cassa)
      if (metodo === 'CardPrepagata') {
        await axios.post(`${API}/clienti/${clienteSelezionato.cliente_id}/card/paga`, {
          importo: totaleFinale,
          descrizione: `${descrizioneCompleta}${scontoApplicato > 0 ? ` (sconto €${scontoApplicato.toFixed(2)})` : ''}`,
          appuntamento_id: clienteSelezionato.id,
          // Nuovi campi per report cassa completo
          operatore: operatoreSelezionato || null,
          cliente_nome: clienteSelezionato.cliente_nome,
          servizi_dettaglio: serviziDettaglioTx,
          importo_servizi: subtotaleServizi,
          importo_prodotti: subtotaleProdotti,
          prodotti_venduti: prodottiSelezionati.map(p => ({
            prodotto_id: p.id,
            nome: p.nome,
            quantita: p.quantita,
            prezzo_unitario: p.prezzo_vendita,
            totale: p.prezzo_vendita * p.quantita
          })),
          sconto: scontoApplicato > 0 ? scontoApplicato : null,
          sconto_tipo: scontoTipo !== "nessuno" ? scontoTipo : null,
          sconto_valore: scontoValore ? parseFloat(scontoValore) : null
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Scarica prodotti dal magazzino anche per card prepagata
        for (const prodotto of prodottiSelezionati) {
          await axios.patch(`${API}/prodotti/${prodotto.id}/quantita?delta=${-prodotto.quantita}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        toast.success(`Pagamento con Card Prepagata! Nuovo saldo: €${(cardSaldo - totaleFinale).toFixed(2)}`);
        setIncassoCompletato({ cliente: clienteSelezionato?.cliente_nome || 'Cliente', totale: totaleFinale, metodo });
      } else {
        // Registra la transazione normale con prezzi, sconto e prodotti
        await axios.post(`${API}/transazioni`, {
          tipo: "entrata",
          descrizione: `${descrizioneCompleta} - ${clienteSelezionato.cliente_nome}${scontoApplicato > 0 ? ` (sconto €${scontoApplicato.toFixed(2)})` : ''}`,
          importo: totaleFinale,
          metodo_pagamento: metodo.toLowerCase(),
          cliente_id: clienteSelezionato.cliente_id,
          cliente_nome: clienteSelezionato.cliente_nome,
          appuntamento_id: clienteSelezionato.id || null,
          operatore: operatoreSelezionato || null,
          data: format(selectedDate, "yyyy-MM-dd"),
          sconto: scontoApplicato > 0 ? scontoApplicato : null,
          sconto_tipo: scontoTipo !== "nessuno" ? scontoTipo : null,
          sconto_valore: scontoValore ? parseFloat(scontoValore) : null,
          importo_servizi: subtotaleServizi,
          importo_prodotti: subtotaleProdotti,
          servizi_dettaglio: serviziDettaglioTx,
          prodotti_venduti: prodottiSelezionati.map(p => ({
            prodotto_id: p.id,
            nome: p.nome,
            quantita: p.quantita,
            prezzo_unitario: p.prezzo_vendita,
            totale: p.prezzo_vendita * p.quantita
          }))
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Aggiorna stato appuntamento a completato SOLO se esiste un appuntamento collegato
        if (clienteSelezionato.id) {
          await axios.put(
            `${API}/appuntamenti/${clienteSelezionato.id}/stato?stato=completato`,
            {
              totale_pagato: totaleFinale,
              servizi_finali: serviziFinali
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        
        // Scarica prodotti dal magazzino
        for (const prodotto of prodottiSelezionati) {
          await axios.patch(`${API}/prodotti/${prodotto.id}/quantita?delta=${-prodotto.quantita}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        
        const msgProdotti = prodottiSelezionati.length > 0 ? ` + ${prodottiSelezionati.length} prodotti` : '';
        const nomeClienteIncassato = clienteSelezionato?.cliente_nome || 'Cliente';
        toast.success(`Pagamento ${metodo} registrato: €${totaleFinale.toFixed(2)}${msgProdotti}${operatoreSelezionato ? ` (${operatoreSelezionato})` : ''}!`, {
          duration: 8000,
          action: {
            label: '📅 Torna in Agenda',
            onClick: () => navigate('/agenda')
          }
        });
        // Mostra anche dialog conferma con bottone evidente
        setIncassoCompletato({ cliente: nomeClienteIncassato, totale: totaleFinale, metodo });
      }

      // Chiudi modale con animazione safe
      requestAnimationFrame(() => {
        setMostraCassa(false);
        setClienteSelezionato(null);
        setCardSaldo(null);
        setPrezziModificati({});
        setScontoTipo("nessuno");
        setScontoValore("");
        setProdottiSelezionati([]);
        setServiziAggiunti([]);
        setShowServizi(false);
        setShowProdotti(false);
        
        // Aggiorna dati con delay per evitare conflitti DOM
        setTimeout(() => {
          fetchAppuntamenti();
          fetchCorrispettivi();
          fetchProdottiRivendita();
        }, 150);
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel pagamento");
    }
  };

  // Apre la cassa per una "vendita libera" (cliente senza appuntamento)
  const openCassaLibera = async (cliente) => {
    const clienteFinto = {
      id: null,  // nessun appuntamento collegato
      cliente_id: cliente.id,
      cliente_nome: `${cliente.nome || ''} ${cliente.cognome || ''}`.trim(),
      operatore_nome: null,
      servizi: [],  // l'utente aggiungerà servizi dal listino
      totale: 0
    };
    setShowRicercaCliente(false);
    setSearchCliente("");
    await openCassa(clienteFinto);
  };

  // Crea un nuovo cliente al volo + apre cassa
  const creaClienteRapido = async () => {
    if (!nuovoCliente.nome.trim() || !nuovoCliente.cognome.trim() || !nuovoCliente.telefono.trim()) {
      toast.error("Nome, cognome e telefono sono obbligatori");
      return;
    }
    if (!nuovoCliente.genere || !["M", "F"].includes(nuovoCliente.genere.toUpperCase())) {
      toast.error("Seleziona il sesso (M / F) del cliente");
      return;
    }
    setCreatingCliente(true);
    try {
      const r = await axios.post(`${API}/clienti`, {
        nome: nuovoCliente.nome.trim(),
        cognome: nuovoCliente.cognome.trim(),
        telefono: nuovoCliente.telefono.trim(),
        genere: nuovoCliente.genere.toUpperCase(),
        is_starred: !!nuovoCliente.is_starred
      }, { headers: { Authorization: `Bearer ${token}` } });
      const created = r.data;
      toast.success(`Cliente ${created.nome} ${created.cognome} creato${nuovoCliente.is_starred ? ' ⭐' : ''}`);
      // Reset form e aggiungi alla cache locale
      setNuovoCliente({ nome: "", cognome: "", telefono: "", genere: "", is_starred: true });
      setClientiTutti(prev => [created, ...prev]);
      setShowCreaCliente(false);
      // Apri direttamente la cassa con questo cliente
      await openCassaLibera(created);
    } catch (error) {
      const msg = error?.response?.data?.detail || "Errore nella creazione cliente";
      toast.error(msg);
    } finally {
      setCreatingCliente(false);
    }
  };

  // Carica clienti per ricerca
  const fetchClientiTutti = async () => {
    if (clientiTutti.length > 0) return; // già caricati
    setLoadingClienti(true);
    try {
      const r = await axios.get(`${API}/clienti`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientiTutti(r.data || []);
    } catch (error) {
      toast.error("Errore nel caricamento clienti");
    } finally {
      setLoadingClienti(false);
    }
  };

  // Filtra clienti per ricerca (case-insensitive su nome/cognome/telefono)
  const clientiFiltrati = (() => {
    if (!searchCliente.trim()) return clientiTutti.slice(0, 50);
    const q = searchCliente.toLowerCase().trim();
    return clientiTutti
      .filter(c => 
        `${c.nome || ''} ${c.cognome || ''}`.toLowerCase().includes(q) ||
        (c.telefono || '').toLowerCase().includes(q)
      )
      .slice(0, 50);
  })();

  const openCassa = async (appuntamento) => {
    setClienteSelezionato(appuntamento);
    setMostraCassa(true);
    setLoadingCard(true);
    
    // Pre-seleziona l'operatore dall'appuntamento (se presente)
    if (appuntamento.operatore_nome) {
      // Cerca il nome dell'operatore (solo il nome, senza cognome)
      const nomeOp = appuntamento.operatore_nome.split(' ')[0].toUpperCase();
      const operatore = operatori.find(op => 
        op.nome.toUpperCase() === nomeOp || 
        op.nome.toUpperCase().includes(nomeOp) ||
        appuntamento.operatore_nome.toUpperCase().includes(op.nome.toUpperCase())
      );
      if (operatore) {
        setOperatoreSelezionato(operatore.nome);
      } else {
        // Se non trova corrispondenza esatta, usa il nome dall'appuntamento
        setOperatoreSelezionato(appuntamento.operatore_nome.split(' ')[0]);
      }
    } else {
      setOperatoreSelezionato("");
    }
    
    // Inizializza i prezzi modificati con i prezzi originali
    const prezziInit = {};
    appuntamento.servizi?.forEach((s, idx) => {
      prezziInit[idx] = s.prezzo || 0;
    });
    setPrezziModificati(prezziInit);
    setScontoTipo("nessuno");
    setScontoValore("");
    setEditingServizio(null);
    setProdottiSelezionati([]); // Reset prodotti selezionati
    setShowProdotti(false);
    setSearchProdotto("");
    setEditingProdotto(null);
    setServiziAggiunti([]); // Reset servizi aggiunti
    setServiziRimossi(new Set()); // Reset servizi rimossi
    // Se vendita libera (nessun appuntamento), apri subito il selettore servizi
    setShowServizi(!appuntamento.id && (!appuntamento.servizi || appuntamento.servizi.length === 0));
    setSearchServizio("");
    setShowScanner(false);
    
    // Verifica se il cliente ha una card prepagata
    try {
      const res = await axios.get(`${API}/clienti/${appuntamento.cliente_id}/card`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data && res.data.saldo !== undefined) {
        setCardSaldo(res.data.saldo);
      } else {
        setCardSaldo(null);
      }
    } catch {
      setCardSaldo(null);
    } finally {
      setLoadingCard(false);
    }
  };
  
  // Funzioni per gestire i prodotti
  const aggiungiProdotto = (prodotto) => {
    const esistente = prodottiSelezionati.find(p => p.id === prodotto.id);
    if (esistente) {
      // Verifica disponibilità
      if (esistente.quantita >= prodotto.quantita) {
        toast.error("Quantità massima raggiunta");
        return;
      }
      setProdottiSelezionati(prodottiSelezionati.map(p => 
        p.id === prodotto.id ? {...p, quantita: p.quantita + 1} : p
      ));
    } else {
      setProdottiSelezionati([...prodottiSelezionati, {
        ...prodotto,
        quantita: 1,
        prezzo_vendita: prodotto.prezzo_vendita || prodotto.prezzo || 0
      }]);
    }
    toast.success(`${prodotto.nome} aggiunto`);
  };
  
  const rimuoviProdotto = (prodottoId) => {
    const esistente = prodottiSelezionati.find(p => p.id === prodottoId);
    if (esistente && esistente.quantita > 1) {
      setProdottiSelezionati(prodottiSelezionati.map(p => 
        p.id === prodottoId ? {...p, quantita: p.quantita - 1} : p
      ));
    } else {
      setProdottiSelezionati(prodottiSelezionati.filter(p => p.id !== prodottoId));
    }
  };
  
  const modificaPrezzoProdotto = (prodottoId, nuovoPrezzo) => {
    setProdottiSelezionati(prodottiSelezionati.map(p => 
      p.id === prodottoId ? {...p, prezzo_vendita: parseFloat(nuovoPrezzo) || 0} : p
    ));
  };
  
  // Filtra prodotti per ricerca
  const prodottiFiltrati = prodottiDisponibili.filter(p => 
    p.nome.toLowerCase().includes(searchProdotto.toLowerCase()) ||
    p.marca?.toLowerCase().includes(searchProdotto.toLowerCase()) ||
    p.codice_barre?.includes(searchProdotto)
  );
  
  // Funzioni per gestire i servizi aggiuntivi (toggle: aggiungi/rimuovi)
  const aggiungiServizio = (servizio) => {
    // Se già presente fra i servizi aggiunti, lo rimuove (toggle)
    const esistente = serviziAggiunti.find(s => s.nome === servizio.nome);
    if (esistente) {
      rimuoviServizioAggiunto(esistente.idx);
      toast.info(`${servizio.nome} rimosso`);
      return;
    }
    // Calcola nuovo indice univoco evitando collisioni con servizi originali
    const usedIdx = new Set([
      ...((clienteSelezionato?.servizi || []).map((_, i) => i)),
      ...serviziAggiunti.map(s => s.idx)
    ]);
    let nuovoIdx = (clienteSelezionato?.servizi?.length || 0);
    while (usedIdx.has(nuovoIdx)) nuovoIdx += 1;
    setServiziAggiunti([...serviziAggiunti, {
      ...servizio,
      idx: nuovoIdx
    }]);
    setPrezziModificati({
      ...prezziModificati,
      [nuovoIdx]: servizio.prezzo || 0
    });
    toast.success(`${servizio.nome} aggiunto`);
  };
  
  const rimuoviServizioAggiunto = (idx) => {
    setServiziAggiunti(serviziAggiunti.filter(s => s.idx !== idx));
    const nuoviPrezzi = {...prezziModificati};
    delete nuoviPrezzi[idx];
    setPrezziModificati(nuoviPrezzi);
  };

  // Toggle rimozione servizio ORIGINALE (dall'appuntamento). Non elimina dall'appuntamento,
  // ma lo esclude dalla cassa e dal totale.
  const toggleRimuoviServizioOriginale = (idx) => {
    setServiziRimossi(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
        toast.info('Servizio ripristinato');
      } else {
        next.add(idx);
        toast.info('Servizio escluso dal totale');
      }
      return next;
    });
  };
  
  // Scanner barcode
  const handleBarcodeScan = async (barcode) => {
    const prodotto = prodottiDisponibili.find(p => p.codice_barre === barcode);
    if (prodotto) {
      aggiungiProdotto(prodotto);
      setShowScanner(false);
      toast.success(`Prodotto scansionato: ${prodotto.nome}`);
    } else {
      toast.error(`Prodotto non trovato: ${barcode}`);
    }
  };
  
  // Calcola totale con sconti
  const calcolaTotaleFinale = () => {
    if (!clienteSelezionato) return 0;
    
    // Somma prezzi modificati servizi (originali + aggiunti), esclusi i servizi rimossi
    const subtotaleServizi = Object.entries(prezziModificati).reduce((sum, [k, p]) => {
      if (serviziRimossi.has(Number(k))) return sum;
      return sum + (parseFloat(p) || 0);
    }, 0);
    
    // Somma prezzi prodotti
    const subtotaleProdotti = prodottiSelezionati.reduce((sum, p) => sum + (p.prezzo_vendita * p.quantita), 0);
    
    const subtotale = subtotaleServizi + subtotaleProdotti;
    
    // Applica sconto
    if (scontoTipo === "percentuale" && scontoValore) {
      const sconto = subtotale * (parseFloat(scontoValore) / 100);
      return Math.max(0, subtotale - sconto);
    } else if (scontoTipo === "importo" && scontoValore) {
      return Math.max(0, subtotale - parseFloat(scontoValore));
    }
    
    return subtotale;
  };
  
  // Calcola subtotale servizi (originali + aggiunti, esclusi rimossi)
  const getSubtotaleServizi = () => {
    return Object.entries(prezziModificati).reduce((sum, [k, p]) => {
      if (serviziRimossi.has(Number(k))) return sum;
      return sum + (parseFloat(p) || 0);
    }, 0);
  };
  
  // Calcola subtotale prodotti
  const getSubtotaleProdotti = () => {
    return prodottiSelezionati.reduce((sum, p) => sum + (p.prezzo_vendita * p.quantita), 0);
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  const isToday = format(selectedDate, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");

  // Calcola totali
  const totaleDaPagare = appuntamenti.filter(a => a.stato === 'confermato').reduce((sum, a) => sum + a.totale, 0);
  const totalePagato = appuntamenti.filter(a => a.stato === 'completato').reduce((sum, a) => sum + a.totale, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="cassa">
      {/* Date Navigation */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={() => navigateDate(-1)}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-center">
              <h3 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Playfair Display, serif' }}>
                {format(selectedDate, "EEEE d MMMM yyyy", { locale: it })}
              </h3>
              {isToday && <span className="text-sm text-blue-600 font-medium">Oggi</span>}
            </div>
            <Button variant="ghost" size="icon" onClick={() => navigateDate(1)}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Vendita Rapida - pulsante prominente in alto */}
      <div className="max-w-2xl mx-auto">
        <Button
          onClick={() => {
            setShowRicercaCliente(true);
            fetchClientiTutti();
          }}
          className="w-full gap-2 bg-blue-600 hover:bg-blue-700 text-white h-12 text-base font-semibold shadow-md"
          data-testid="apri-vendita-libera-btn"
        >
          <Search className="w-5 h-5" />
          Vendita Rapida — Cerca Cliente
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
        <div className="bg-emerald-50 rounded-3xl p-6 text-center border border-emerald-200">
          <p className="text-emerald-600 font-bold text-xs uppercase tracking-widest mb-2">Incassato</p>
          <p className="text-4xl font-black text-emerald-700">€{totalePagato.toFixed(2)}</p>
        </div>
        <div className="bg-amber-50 rounded-3xl p-6 text-center border border-amber-200">
          <p className="text-amber-600 font-bold text-xs uppercase tracking-widest mb-2">Da Incassare</p>
          <p className="text-4xl font-black text-amber-700">€{totaleDaPagare.toFixed(2)}</p>
        </div>
      </div>

      {/* Corrispettivi Toggle Button */}
      <div className="max-w-2xl mx-auto">
        <Button
          variant="outline"
          onClick={() => setShowCorrispettivi(!showCorrispettivi)}
          className="w-full gap-2 text-slate-600"
          data-testid="toggle-corrispettivi"
        >
          <Calculator className="w-4 h-4" />
          {showCorrispettivi ? 'Nascondi' : 'Mostra'} Corrispettivi Giornalieri
        </Button>
      </div>

      {/* Corrispettivi Giornalieri - Design Migliorato */}
      {showCorrispettivi && corrispettivi && (
        <Card className="max-w-3xl mx-auto border-0 shadow-xl bg-white overflow-hidden">
          {/* Header con gradiente */}
          <div className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                  <Receipt className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Corrispettivi Giornalieri</h3>
                  <p className="text-slate-300 text-sm">{format(selectedDate, "EEEE d MMMM yyyy", { locale: it })}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-slate-400 text-xs uppercase tracking-wider">Totale Incassato</p>
                <p className="text-4xl font-bold">€{(corrispettivi.totale_giornaliero ?? 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
          </div>

          <CardContent className="p-6 space-y-6">
            {/* Statistiche rapide */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-200">
                <Banknote className="w-5 h-5 text-emerald-600 mb-2" />
                <p className="text-xs text-emerald-700 font-medium">Contanti</p>
                <p className="text-xl font-bold text-emerald-800">€{corrispettivi.per_metodo?.contanti?.totale ?? 0}</p>
                <p className="text-xs text-emerald-600">{corrispettivi.per_metodo?.contanti?.num ?? 0} operazioni</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                <CreditCard className="w-5 h-5 text-blue-600 mb-2" />
                <p className="text-xs text-blue-700 font-medium">Carta/POS</p>
                <p className="text-xl font-bold text-blue-800">€{corrispettivi.per_metodo?.carta?.totale ?? 0}</p>
                <p className="text-xs text-blue-600">{corrispettivi.per_metodo?.carta?.num ?? 0} operazioni</p>
              </div>
              <div className="bg-gradient-to-br from-violet-50 to-violet-100 rounded-xl p-4 border border-violet-200">
                <Gift className="w-5 h-5 text-violet-600 mb-2" />
                <p className="text-xs text-violet-700 font-medium">Card Prepagata</p>
                <p className="text-xl font-bold text-violet-800">€{corrispettivi.per_metodo?.uso_card_prepagata?.totale ?? 0}</p>
                <p className="text-xs text-violet-600">{corrispettivi.per_metodo?.uso_card_prepagata?.num ?? 0} utilizzi</p>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200">
                <Wallet className="w-5 h-5 text-amber-600 mb-2" />
                <p className="text-xs text-amber-700 font-medium">Ricariche Card</p>
                <p className="text-xl font-bold text-amber-800">€{corrispettivi.per_metodo?.ricariche_card?.totale ?? 0}</p>
                <p className="text-xs text-amber-600">{corrispettivi.per_metodo?.ricariche_card?.num ?? 0} ricariche</p>
              </div>
            </div>

            {/* Nota contabile */}
            {(corrispettivi.per_metodo?.uso_card_prepagata?.totale ?? 0) > 0 && (
              <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-violet-600 shrink-0 mt-0.5" />
                <div className="text-sm text-violet-800">
                  <p className="font-medium">Nota Contabile</p>
                  <p className="text-violet-600 text-xs mt-1">
                    I pagamenti con Card Prepagata (€{corrispettivi.per_metodo?.uso_card_prepagata?.totale ?? 0}) non sono nuove entrate - il cliente sta usando credito già incassato con le ricariche.
                  </p>
                </div>
              </div>
            )}

            {/* Riepilogo numerico */}
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-slate-800">{corrispettivi.num_transazioni ?? 0}</p>
                  <p className="text-xs text-slate-500">Transazioni</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">
                    €{((corrispettivi.per_metodo?.contanti?.totale ?? 0) + (corrispettivi.per_metodo?.carta?.totale ?? 0) + (corrispettivi.per_metodo?.ricariche_card?.totale ?? 0)).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-slate-500">Entrate Reali</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-violet-600">€{corrispettivi.per_metodo?.uso_card_prepagata?.totale ?? 0}</p>
                  <p className="text-xs text-slate-500">Uso Card Prep.</p>
                </div>
              </div>
            </div>

            {/* Link al report dettagliato in Analisi */}
            <Button
              variant="outline"
              onClick={() => navigate('/analisi?tab=operatori')}
              className="w-full gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
              data-testid="link-analisi-operatori"
            >
              <BarChart3 className="w-4 h-4" />
              Vedi report dettagliato operatori in Analisi
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Cassa Rapida */}
      <div className="max-w-2xl mx-auto">
        <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
          Cassa Rapida
        </h2>
        
        {appuntamenti.length > 0 ? (
          <div className="grid gap-4">
            {appuntamenti.map((a, index) => (
              <div 
                key={a.id} 
                className={`bg-white p-5 rounded-3xl shadow-sm border border-slate-200 flex justify-between items-center animate-fadeIn stagger-${Math.min(index + 1, 5)}`}
                data-testid={`cassa-item-${a.id}`}
              >
                <div>
                  <p className="font-black text-lg text-slate-800">{a.cliente_nome}</p>
                  <p className="text-sm text-slate-500">{a.ora} • {a.servizi.map(s => s.nome).join(' + ')}</p>
                  <p className="text-blue-600 font-bold text-xl mt-1">€{a.totale.toFixed(2)}</p>
                </div>
                
                {a.stato === 'completato' ? (
                  <div className="flex items-center gap-2 bg-emerald-50 text-emerald-600 px-4 py-2 rounded-2xl font-black">
                    <Check className="w-5 h-5" />
                    PAGATO
                  </div>
                ) : a.stato === 'cancellato' ? (
                  <div className="flex items-center gap-2 bg-slate-100 text-slate-400 px-4 py-2 rounded-2xl font-black">
                    CANCELLATO
                  </div>
                ) : (
                  <button 
                    onClick={() => openCassa(a)}
                    className="bg-slate-900 text-white px-6 py-3 rounded-2xl font-bold hover:bg-blue-600 transition-colors shadow-lg"
                    data-testid={`incassa-${a.id}`}
                  >
                    INCASSA
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-12 text-center">
            <p className="text-slate-400 text-xl italic">Nessun appuntamento per questa data</p>
          </div>
        )}
      </div>

      {/* MODALE POPUP PAGAMENTO - COMPLETO E IN ALTO */}
      {mostraCassa && clienteSelezionato && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-start justify-center z-50 pt-2 md:pt-4 overflow-y-auto">
          <div
            className="rounded-2xl w-full max-w-lg shadow-2xl animate-scaleIn mx-2 mb-4 border-4 border-transparent overflow-hidden"
            style={{
              backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #10b981, #14b8a6, #06b6d4)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box'
            }}
          >
            {/* Header con nome cliente */}
            <div className="px-4 pt-3 pb-2 border-b border-slate-100 sticky top-0 bg-white z-10">
              <p className="text-slate-500 font-bold text-xs uppercase tracking-widest text-center">
                {clienteSelezionato.cliente_nome}
              </p>
            </div>
            
            {/* Corpo */}
            <div className="p-3 space-y-2">
              
              {/* SERVIZI con modifica prezzo e aggiungi */}
              <div className="bg-slate-50 rounded-xl p-2">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] text-slate-500 font-semibold flex items-center gap-1">
                    <Scissors className="w-3 h-3" /> Servizi
                  </p>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="h-6 text-[11px] px-2 text-blue-600 hover:bg-blue-50"
                    onClick={() => setShowServizi(true)}
                    data-testid="cassa-apri-selettore-servizi"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Aggiungi servizio
                  </Button>
                </div>
                
                {/* Lista servizi con modifica prezzo */}
                <div className="space-y-1">
                  {clienteSelezionato.servizi?.map((servizio, idx) => {
                    const rimosso = serviziRimossi.has(idx);
                    return (
                    <div
                      key={idx}
                      className={`flex items-center justify-between rounded-lg p-1.5 text-xs transition ${
                        rimosso ? 'bg-red-50 border border-red-200 opacity-60' : 'bg-white'
                      }`}
                      data-testid={`servizio-originale-${idx}`}
                    >
                      <span className={`truncate flex-1 ${rimosso ? 'line-through text-red-500' : 'text-slate-700'}`}>
                        {servizio.nome}
                      </span>
                      {editingServizio === idx && !rimosso ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            step="0.01"
                            value={prezziModificati[idx] ?? ''}
                            onChange={(e) => setPrezziModificati({...prezziModificati, [idx]: e.target.value})}
                            className="w-16 h-6 text-right text-xs"
                            autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') setEditingServizio(null); }}
                            data-testid={`input-prezzo-orig-${idx}`}
                          />
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditingServizio(null)} data-testid={`conferma-prezzo-${idx}`}>
                            <Check className="w-3 h-3 text-green-600" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <span className={`font-bold ${rimosso ? 'text-slate-400 line-through' : (parseFloat(prezziModificati[idx]) !== parseFloat(servizio.prezzo) ? 'text-blue-600' : 'text-slate-800')}`}>
                            €{parseFloat(prezziModificati[idx] ?? servizio.prezzo ?? 0).toFixed(2)}
                          </span>
                          {!rimosso && (
                            <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => setEditingServizio(idx)} data-testid={`edit-prezzo-${idx}`}>
                              <Edit2 className="w-2.5 h-2.5 text-slate-400" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className={`h-5 w-5 p-0 ${rimosso ? 'text-emerald-600' : 'text-red-500'}`}
                            onClick={() => toggleRimuoviServizioOriginale(idx)}
                            title={rimosso ? 'Ripristina servizio' : 'Elimina servizio dalla cassa'}
                            data-testid={`toggle-rimuovi-${idx}`}
                          >
                            {rimosso ? <Check className="w-2.5 h-2.5" /> : <Trash2 className="w-2.5 h-2.5" />}
                          </Button>
                        </div>
                      )}
                    </div>
                    );
                  })}
                  
                  {/* Servizi aggiunti extra */}
                  {serviziAggiunti.map((servizio) => (
                    <div key={servizio.idx} className="flex items-center justify-between bg-blue-50 rounded-lg p-1.5 text-xs border border-blue-200">
                      <span className="text-blue-700 truncate flex-1 flex items-center gap-1">
                        <Plus className="w-2.5 h-2.5" /> {servizio.nome}
                      </span>
                      {editingServizio === servizio.idx ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={prezziModificati[servizio.idx] || ''}
                            onChange={(e) => setPrezziModificati({...prezziModificati, [servizio.idx]: e.target.value})}
                            className="w-14 h-6 text-right text-xs"
                            autoFocus
                          />
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditingServizio(null)}>
                            <Check className="w-3 h-3 text-green-600" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <span className="font-bold text-blue-700">€{parseFloat(prezziModificati[servizio.idx] || 0).toFixed(2)}</span>
                          <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-blue-600" onClick={() => setEditingServizio(servizio.idx)}>
                            <Edit2 className="w-2.5 h-2.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-500" onClick={() => rimuoviServizioAggiunto(servizio.idx)}>
                            <Trash2 className="w-2.5 h-2.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-between items-center mt-1 pt-1 border-t border-slate-200 text-xs">
                  <span className="text-slate-500">Subtotale:</span>
                  <span className="font-bold text-slate-700">€{getSubtotaleServizi().toFixed(2)}</span>
                </div>
              </div>
              
              {/* PRODOTTI con quantità */}
              <div className="bg-purple-50 rounded-xl p-2">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] text-purple-700 font-semibold flex items-center gap-1">
                    <ShoppingBag className="w-3 h-3" /> Prodotti
                  </p>
                  <div className="flex gap-1">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="h-5 text-[10px] px-1 text-purple-700"
                      onClick={() => setShowScanner(true)}
                    >
                      <Camera className="w-3 h-3" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="h-5 text-[10px] px-1 text-purple-700"
                      onClick={() => setShowProdotti(!showProdotti)}
                    >
                      <Plus className="w-3 h-3" /> Aggiungi
                    </Button>
                  </div>
                </div>
                
                {/* Popup aggiungi prodotto */}
                {showProdotti && (
                  <div className="mb-2 p-2 bg-white rounded-lg border border-purple-200">
                    <Input
                      placeholder="Cerca prodotto..."
                      value={searchProdotto}
                      onChange={(e) => setSearchProdotto(e.target.value)}
                      className="h-7 text-xs mb-1"
                    />
                    <div className="max-h-24 overflow-y-auto space-y-1">
                      {prodottiFiltrati.slice(0, 5).map(p => (
                        <button
                          key={p.id}
                          onClick={() => aggiungiProdotto(p)}
                          className="w-full text-left p-1.5 hover:bg-purple-50 rounded text-xs flex justify-between"
                        >
                          <span className="truncate flex-1">{p.nome} <span className="text-slate-400">({p.quantita})</span></span>
                          <span className="text-purple-600 font-bold">€{p.prezzo_vendita || p.prezzo}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Lista prodotti con quantità e modifica prezzo */}
                {prodottiSelezionati.length > 0 ? (
                  <div className="space-y-1">
                    {prodottiSelezionati.map(p => (
                      <div key={p.id} className="flex items-center justify-between bg-white rounded-lg p-1.5 text-xs">
                        <span className="text-slate-700 truncate flex-1">{p.nome}</span>
                        <div className="flex items-center gap-1">
                          <div className="flex items-center bg-slate-100 rounded">
                            <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => rimuoviProdotto(p.id)}>
                              <Minus className="w-2.5 h-2.5" />
                            </Button>
                            <span className="text-xs font-bold w-4 text-center">{p.quantita}</span>
                            <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => aggiungiProdotto(p)}>
                              <Plus className="w-2.5 h-2.5" />
                            </Button>
                          </div>
                          {editingProdotto === p.id ? (
                            <div className="flex items-center gap-0.5">
                              <Input
                                type="number"
                                value={p.prezzo_vendita}
                                onChange={(e) => modificaPrezzoProdotto(p.id, e.target.value)}
                                className="w-12 h-5 text-right text-xs p-1"
                                autoFocus
                              />
                              <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => setEditingProdotto(null)}>
                                <Check className="w-2.5 h-2.5 text-green-600" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-0.5">
                              <span className="font-bold text-purple-700 text-right">€{(p.prezzo_vendita * p.quantita).toFixed(2)}</span>
                              <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => setEditingProdotto(p.id)}>
                                <Edit2 className="w-2 h-2 text-purple-400" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between items-center mt-1 pt-1 border-t border-purple-200 text-xs">
                      <span className="text-purple-600">Subtotale:</span>
                      <span className="font-bold text-purple-700">€{getSubtotaleProdotti().toFixed(2)}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-purple-400 text-[10px] py-1">Nessun prodotto</p>
                )}
              </div>
              
              {/* SCONTO */}
              <div className="flex items-center gap-2 bg-amber-50 rounded-xl p-2">
                <Percent className="w-3 h-3 text-amber-600" />
                <span className="text-[10px] text-amber-700 font-semibold">Sconto:</span>
                <div className="flex gap-1 flex-1">
                  <Button
                    variant={scontoTipo === "nessuno" ? "default" : "outline"}
                    size="sm"
                    className={`h-6 text-[10px] px-2 ${scontoTipo === "nessuno" ? 'bg-slate-600' : ''}`}
                    onClick={() => { setScontoTipo("nessuno"); setScontoValore(""); }}
                  >
                    No
                  </Button>
                  <Button
                    variant={scontoTipo === "percentuale" ? "default" : "outline"}
                    size="sm"
                    className={`h-6 text-[10px] px-2 ${scontoTipo === "percentuale" ? 'bg-amber-600' : ''}`}
                    onClick={() => setScontoTipo("percentuale")}
                  >
                    %
                  </Button>
                  <Button
                    variant={scontoTipo === "importo" ? "default" : "outline"}
                    size="sm"
                    className={`h-6 text-[10px] px-2 ${scontoTipo === "importo" ? 'bg-amber-600' : ''}`}
                    onClick={() => setScontoTipo("importo")}
                  >
                    €
                  </Button>
                  {scontoTipo !== "nessuno" && (
                    <Input
                      type="number"
                      placeholder={scontoTipo === "percentuale" ? "10" : "5"}
                      value={scontoValore}
                      onChange={(e) => setScontoValore(e.target.value)}
                      className="w-14 h-6 text-xs"
                    />
                  )}
                </div>
              </div>
              
              {/* TOTALE */}
              <div className="text-center py-2 bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl">
                <p className="text-[10px] text-slate-400 uppercase">Totale</p>
                <p className="text-3xl font-black text-white">€{calcolaTotaleFinale().toFixed(2)}</p>
              </div>
              
              {/* OPERATORE */}
              <div className="flex items-center gap-2 bg-blue-50 rounded-xl p-2">
                <User className="w-4 h-4 text-blue-500" />
                <span className="text-xs font-semibold text-blue-700">Operatore:</span>
                <Select 
                  value={operatoreSelezionato} 
                  onValueChange={(value) => {
                    requestAnimationFrame(() => setOperatoreSelezionato(value));
                  }}
                >
                  <SelectTrigger className={`flex-1 h-8 text-sm ${!operatoreSelezionato ? 'border-red-300 bg-red-50' : 'bg-white'}`} data-testid="cassa-operatore-select">
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent>
                    {operatori.map(op => (
                      <SelectItem key={op.id} value={op.nome}>
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: op.colore }} />
                          {op.nome}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* PULSANTI PAGAMENTO */}
              {/* ═══════ ALERT SALDO CARD BASSO — Ricarica rapida ═══════ */}
              {cardSaldo !== null && cardSaldo < 20 && clienteSelezionato?.cliente_id && (
                <div
                  className={`rounded-xl p-3 border-2 ${cardSaldo < 5 ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'} flex items-center justify-between gap-2 flex-wrap`}
                  data-testid="alert-saldo-card-basso"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-white shrink-0 ${cardSaldo < 5 ? 'bg-red-500' : 'bg-amber-500'}`}>
                      <CreditCard className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <p className={`text-xs font-bold uppercase tracking-wide ${cardSaldo < 5 ? 'text-red-700' : 'text-amber-700'}`}>
                        {cardSaldo < 5 ? 'Saldo Card CRITICO' : 'Saldo Card basso'}
                      </p>
                      <p className={`text-sm ${cardSaldo < 5 ? 'text-red-800' : 'text-amber-800'}`}>
                        Solo <strong>€{cardSaldo.toFixed(2)}</strong> rimasti — proponi al cliente una ricarica
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setRicaricaImporto(50); setRicaricaMetodo('contanti'); setShowRicaricaRapida(true); }}
                    className={`text-sm font-bold text-white rounded-lg px-4 py-2 shadow ${cardSaldo < 5 ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                    data-testid="btn-ricarica-rapida"
                  >
                    ⚡ Ricarica ora
                  </button>
                </div>
              )}

              <div className="grid grid-cols-3 gap-2">
                <button 
                  onClick={() => { setMetodoPendente('Contanti'); setShowAnteprimaScontrino(true); }} 
                  disabled={!operatoreSelezionato}
                  className={`py-3 rounded-xl font-bold text-sm ${operatoreSelezionato ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                  data-testid="pay-contanti"
                >
                  💵 Contanti
                </button>
                <button 
                  onClick={() => { setMetodoPendente('Carta'); setShowAnteprimaScontrino(true); }} 
                  disabled={!operatoreSelezionato}
                  className={`py-3 rounded-xl font-bold text-sm ${operatoreSelezionato ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                  data-testid="pay-carta"
                >
                  💳 Carta
                </button>
                <button 
                  onClick={() => { if (cardSaldo >= calcolaTotaleFinale()) { setMetodoPendente('CardPrepagata'); setShowAnteprimaScontrino(true); } }}
                  disabled={!operatoreSelezionato || !cardSaldo || cardSaldo < calcolaTotaleFinale()}
                  className={`py-3 rounded-xl font-bold text-sm ${operatoreSelezionato && cardSaldo >= calcolaTotaleFinale() ? 'bg-violet-500 hover:bg-violet-600 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                  data-testid="pay-card"
                >
                  🎫 Card
                </button>
              </div>
              
              {/* ANNULLA */}
              <button 
                onClick={() => { 
                  requestAnimationFrame(() => {
                    setMostraCassa(false); 
                    setClienteSelezionato(null); 
                    setCardSaldo(null); 
                    setOperatoreSelezionato(""); 
                    setPrezziModificati({}); 
                    setScontoTipo("nessuno"); 
                    setScontoValore(""); 
                    setProdottiSelezionati([]); 
                    setServiziAggiunti([]);
                    setShowProdotti(false);
                    setShowServizi(false);
                  });
                }} 
                className="w-full text-slate-400 font-semibold hover:text-red-500 transition-colors text-xs py-1"
                data-testid="pay-cancel"
              >
                ANNULLA
              </button>
            </div>
          </div>
        </div>
      )}

      <BarcodeScanner
        open={showScanner}
        onOpenChange={setShowScanner}
        prodottiDisponibili={prodottiDisponibili}
        onProductFound={aggiungiProdotto}
        onProductNotFound={(barcode) => {
          toast.error(`Prodotto con codice ${barcode} non trovato nel magazzino`);
        }}
      />

      {/* Dialog Ricerca Cliente (Vendita Libera) */}
      <Dialog open={showRicercaCliente} onOpenChange={(open) => {
        setShowRicercaCliente(open);
        if (!open) setSearchCliente("");
      }}>
        <DialogContent className="sm:max-w-lg" data-testid="ricerca-cliente-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Search className="w-5 h-5 text-blue-600" />
              Cerca Cliente per Vendita Rapida
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Nome, cognome o telefono..."
                value={searchCliente}
                onChange={(e) => setSearchCliente(e.target.value)}
                className="pl-10 h-11"
                autoFocus
                data-testid="ricerca-cliente-input"
              />
              {searchCliente && (
                <button
                  onClick={() => setSearchCliente("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-slate-500">
                {searchCliente
                  ? `${clientiFiltrati.length} risultat${clientiFiltrati.length === 1 ? 'o' : 'i'}`
                  : `Mostrando primi ${Math.min(50, clientiTutti.length)} di ${clientiTutti.length} clienti`}
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setNuovoCliente({
                    nome: searchCliente.split(' ')[0] || "",
                    cognome: searchCliente.split(' ').slice(1).join(' ') || "",
                    telefono: "",
                    genere: "",
                    is_starred: true
                  });
                  setShowCreaCliente(true);
                }}
                className="gap-1 text-emerald-600 border-emerald-300 hover:bg-emerald-50 shrink-0"
                data-testid="apri-crea-cliente-btn"
              >
                <Plus className="w-3.5 h-3.5" />
                Nuovo
              </Button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto space-y-1.5 -mx-2 px-2">
              {loadingClienti ? (
                <div className="text-center py-8 text-slate-400">Caricamento clienti...</div>
              ) : clientiFiltrati.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  Nessun cliente trovato
                </div>
              ) : (
                clientiFiltrati.map(c => (
                  <button
                    key={c.id}
                    onClick={() => openCassaLibera(c)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-blue-50 hover:border-blue-300 transition-all text-left"
                    data-testid={`ricerca-cliente-item-${c.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${
                      c.genere === 'F' ? 'bg-pink-500' : c.genere === 'M' ? 'bg-blue-500' : 'bg-slate-500'
                    }`}>
                      {(c.nome || '').charAt(0)}{(c.cognome || '').charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 break-words line-clamp-1 flex items-center gap-1.5">
                        {c.nome} {c.cognome}
                        {c.is_starred && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 shrink-0" />}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {c.telefono || 'Nessun telefono'}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                  </button>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Crea Nuovo Cliente Rapido */}
      <Dialog open={showCreaCliente} onOpenChange={setShowCreaCliente}>
        <DialogContent className="sm:max-w-md" data-testid="crea-cliente-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Plus className="w-5 h-5 text-emerald-600" />
              Nuovo Cliente Rapido
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Nome *</Label>
                <Input
                  value={nuovoCliente.nome}
                  onChange={(e) => setNuovoCliente({ ...nuovoCliente, nome: e.target.value })}
                  placeholder="Nome"
                  data-testid="crea-cliente-nome"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-xs">Cognome *</Label>
                <Input
                  value={nuovoCliente.cognome}
                  onChange={(e) => setNuovoCliente({ ...nuovoCliente, cognome: e.target.value })}
                  placeholder="Cognome"
                  data-testid="crea-cliente-cognome"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Telefono *</Label>
              <Input
                type="tel"
                value={nuovoCliente.telefono}
                onChange={(e) => setNuovoCliente({ ...nuovoCliente, telefono: e.target.value })}
                placeholder="+39 333 123 4567"
                data-testid="crea-cliente-telefono"
              />
            </div>
            <div>
              <Label className="text-xs">Sesso <span className="text-red-500 font-bold">*</span></Label>
              <Select
                value={nuovoCliente.genere || ""}
                onValueChange={(v) => setNuovoCliente({ ...nuovoCliente, genere: v })}
              >
                <SelectTrigger data-testid="crea-cliente-genere" className={!nuovoCliente.genere ? "border-red-300" : ""}>
                  <SelectValue placeholder="Seleziona M / F (obbligatorio)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="F">👩 Donna</SelectItem>
                  <SelectItem value="M">👨 Uomo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <label className="flex items-center gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50 cursor-pointer hover:bg-amber-100 transition" data-testid="crea-cliente-stellina-label">
              <input
                type="checkbox"
                checked={!!nuovoCliente.is_starred}
                onChange={(e) => setNuovoCliente({ ...nuovoCliente, is_starred: e.target.checked })}
                className="w-4 h-4 accent-amber-500"
                data-testid="crea-cliente-stellina"
              />
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                <span className="text-sm font-medium text-amber-800">Segna come &quot;Cliente Nuovo&quot;</span>
              </div>
            </label>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowCreaCliente(false)}
                className="flex-1"
                disabled={creatingCliente}
              >
                Annulla
              </Button>
              <Button
                onClick={creaClienteRapido}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={creatingCliente}
                data-testid="crea-cliente-conferma-btn"
              >
                {creatingCliente ? "Creazione..." : "Crea e apri Cassa"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Selezione Servizi (vista a CATEGORIE) - estratto */}
      <CassaServiziDialog
        open={showServizi}
        onOpenChange={(open) => {
          setShowServizi(open);
          if (!open) setSearchServizio("");
        }}
        servizi={serviziDisponibili}
        serviziAggiunti={serviziAggiunti}
        prezziModificati={prezziModificati}
        onToggle={aggiungiServizio}
        onConferma={() => setShowServizi(false)}
      />

      {/* Dialog conferma incasso - estratto */}
      <IncassoCompletatoDialog
        incasso={incassoCompletato}
        onClose={() => setIncassoCompletato(null)}
        onTornaAgenda={() => { setIncassoCompletato(null); navigate('/agenda'); }}
      />

      {/* Dialog Remind Anagrafica Incompleta — appare prima del pagamento se mancano telefono/data_nascita */}
      <Dialog open={anagraficaAlertOpen} onOpenChange={setAnagraficaAlertOpen}>
        <DialogContent className="!max-w-md w-[95vw] p-5" data-testid="dialog-anagrafica-mancante">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <span className="text-2xl">⚠️</span>
              Anagrafica incompleta
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-slate-700">
                Per <span className="font-semibold">{anagraficaCliente?.nome} {anagraficaCliente?.cognome}</span> mancano:
              </p>
              <ul className="mt-2 space-y-1">
                {anagraficaCampiMancanti.map(c => (
                  <li key={c} className="text-sm font-medium text-amber-700 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> {c}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-500 mt-2">
                Completali ora per migliorare il marketing (auguri compleanno, promemoria, fidelity).
              </p>
            </div>

            <div className="space-y-2">
              {anagraficaCampiMancanti.includes("telefono") && (
                <div>
                  <Label className="text-xs">Telefono</Label>
                  <Input
                    type="tel"
                    placeholder="Es: 3331234567"
                    value={quickEditAnagrafica.telefono}
                    onChange={(e) => setQuickEditAnagrafica({...quickEditAnagrafica, telefono: e.target.value})}
                    data-testid="input-anagrafica-telefono"
                  />
                </div>
              )}
              {anagraficaCampiMancanti.includes("data nascita") && (
                <div>
                  <Label className="text-xs">Data nascita / compleanno</Label>
                  <Input
                    type="date"
                    value={quickEditAnagrafica.data_nascita}
                    onChange={(e) => setQuickEditAnagrafica({...quickEditAnagrafica, data_nascita: e.target.value})}
                    data-testid="input-anagrafica-data-nascita"
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                variant="outline"
                onClick={handleIgnoraEPaga}
                className="flex-1 border-slate-300 text-slate-600"
                data-testid="btn-ignora-anagrafica"
              >
                Ignora e procedi
              </Button>
              <Button
                onClick={handleSalvaAnagraficaEPaga}
                disabled={savingAnagrafica}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-completa-anagrafica"
              >
                {savingAnagrafica ? "Salvataggio..." : "Completa & Paga"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Ricarica Rapida Card — alert saldo basso */}
      <Dialog open={showRicaricaRapida} onOpenChange={setShowRicaricaRapida}>
        <DialogContent className="!max-w-sm w-[95vw]" data-testid="dialog-ricarica-rapida">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-600" />
              Ricarica Card
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 rounded-lg p-3 text-sm">
              <p className="font-semibold text-amber-900">
                {clienteSelezionato?.cliente_nome || 'Cliente'}
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Saldo attuale: <strong>€{(cardSaldo || 0).toFixed(2)}</strong>
              </p>
            </div>

            <div>
              <Label className="text-xs font-semibold">Importo ricarica</Label>
              <div className="flex gap-1 mt-1">
                {[30, 50, 100, 150, 200].map(v => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setRicaricaImporto(v)}
                    className={`flex-1 py-1.5 rounded border text-sm font-semibold ${ricaricaImporto === v ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-700 border-slate-300 hover:border-amber-300'}`}
                    data-testid={`quick-ricarica-${v}`}
                  >
                    €{v}
                  </button>
                ))}
              </div>
              <Input
                type="number"
                min="1"
                step="10"
                value={ricaricaImporto}
                onChange={(e) => setRicaricaImporto(parseFloat(e.target.value) || 0)}
                className="mt-2 text-right font-bold"
                data-testid="input-ricarica-importo"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold">Metodo di pagamento</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setRicaricaMetodo('contanti')}
                  className={`py-2 rounded border text-sm font-semibold ${ricaricaMetodo === 'contanti' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white border-slate-300'}`}
                  data-testid="ricarica-metodo-contanti"
                >
                  💵 Contanti
                </button>
                <button
                  type="button"
                  onClick={() => setRicaricaMetodo('carta')}
                  className={`py-2 rounded border text-sm font-semibold ${ricaricaMetodo === 'carta' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white border-slate-300'}`}
                  data-testid="ricarica-metodo-carta"
                >
                  💳 Carta / POS
                </button>
              </div>
            </div>

            <p className="text-[10px] text-slate-500 text-center">
              Nuovo saldo dopo ricarica: <strong className="text-emerald-700">€{((cardSaldo || 0) + (ricaricaImporto || 0)).toFixed(2)}</strong>
            </p>

            <div className="flex gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setShowRicaricaRapida(false)} className="flex-1">Annulla</Button>
              <Button
                disabled={savingRicarica || ricaricaImporto <= 0 || !operatoreSelezionato}
                onClick={async () => {
                  setSavingRicarica(true);
                  try {
                    const opNome = operatori.find(o => o.id === operatoreSelezionato)?.nome || '';
                    const r = await axios.post(
                      `${API}/clienti/${clienteSelezionato.cliente_id}/card/ricarica`,
                      {
                        importo: ricaricaImporto,
                        descrizione: `Ricarica rapida €${ricaricaImporto} — ${clienteSelezionato.cliente_nome || 'Cliente'}`,
                        metodo_pagamento: ricaricaMetodo,
                        operatore_id: operatoreSelezionato,
                        operatore_nome: opNome
                      },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    setCardSaldo(r.data.nuovo_saldo);
                    toast.success(`Ricarica €${ricaricaImporto} salvata — nuovo saldo €${r.data.nuovo_saldo.toFixed(2)}`);
                    setShowRicaricaRapida(false);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Errore durante la ricarica");
                  } finally {
                    setSavingRicarica(false);
                  }
                }}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                data-testid="btn-conferma-ricarica"
              >
                {savingRicarica ? '...' : `💾 Ricarica €${ricaricaImporto}`}
              </Button>
            </div>
            {!operatoreSelezionato && (
              <p className="text-[10px] text-red-600 text-center">⚠️ Seleziona un operatore per procedere</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ══════════ DIALOG ANTEPRIMA SCONTRINO — Preview prima di confermare ══════════ */}
      <Dialog open={showAnteprimaScontrino} onOpenChange={setShowAnteprimaScontrino}>
        <DialogContent className="!max-w-md w-[95vw] max-h-[90vh] overflow-y-auto p-0" data-testid="dialog-anteprima-scontrino">
          {clienteSelezionato && (() => {
            const originaliAttivi = (clienteSelezionato.servizi || [])
              .map((s, idx) => ({ s, idx }))
              .filter(({ idx }) => !serviziRimossi.has(idx));
            const totale = calcolaTotaleFinale();
            const subtotServizi = originaliAttivi.reduce((sum, { idx, s }) => {
              const p = prezziModificati[idx];
              return sum + (parseFloat(p !== undefined && p !== '' ? p : s.prezzo) || 0);
            }, 0) + serviziAggiunti.reduce((sum, sa) => {
              const p = prezziModificati[sa.idx];
              return sum + (parseFloat(p !== undefined && p !== '' ? p : sa.prezzo) || 0);
            }, 0);
            const subtotProdotti = prodottiSelezionati.reduce((sum, p) => sum + (p.prezzo_vendita * p.quantita), 0);
            const subtotale = subtotServizi + subtotProdotti;
            const scontoApplicato = subtotale - totale;
            const opNome = operatori.find(o => o.id === operatoreSelezionato)?.nome || '';
            const metodoLabel = metodoPendente === 'Contanti' ? '💵 Contanti' : metodoPendente === 'Carta' ? '💳 Carta' : metodoPendente === 'CardPrepagata' ? '🎫 Card Prepagata' : metodoPendente;

            return (
              <>
                {/* Header */}
                <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-4 rounded-t-lg">
                  <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                      🧾 Anteprima Scontrino
                    </DialogTitle>
                  </DialogHeader>
                  <p className="text-xs text-slate-300 mt-1">Verifica i dettagli prima di confermare il pagamento</p>
                </div>

                {/* Scontrino stile termica */}
                <div className="p-4 bg-white space-y-3 font-mono text-sm">
                  {/* Intestazione */}
                  <div className="text-center border-b-2 border-dashed border-slate-300 pb-2">
                    <p className="font-bold text-base">I CIRILLO PARRUCCHIERI</p>
                    <p className="text-[10px] text-slate-500">
                      {new Date().toLocaleDateString('it-IT')} · {new Date().toLocaleTimeString('it-IT', {hour:'2-digit', minute:'2-digit'})}
                    </p>
                  </div>

                  {/* Cliente + operatore */}
                  <div className="text-xs space-y-0.5">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Cliente:</span>
                      <strong className="text-slate-800">{clienteSelezionato.cliente_nome || 'Cliente rapido'}</strong>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Operatore:</span>
                      <strong className="text-slate-800">{opNome}</strong>
                    </div>
                  </div>

                  {/* Servizi */}
                  {(originaliAttivi.length > 0 || serviziAggiunti.length > 0) && (
                    <div className="border-t border-dashed border-slate-300 pt-2">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Servizi</p>
                      {originaliAttivi.map(({ s, idx }) => {
                        const prezzoFinale = prezziModificati[idx] !== undefined && prezziModificati[idx] !== ''
                          ? parseFloat(prezziModificati[idx]) : (s.prezzo || 0);
                        const modificato = parseFloat(prezziModificati[idx]) !== parseFloat(s.prezzo);
                        return (
                          <div key={`orig-${idx}`} className="flex justify-between text-xs py-0.5">
                            <span className="text-slate-700 truncate flex-1">
                              {s.nome}
                              {modificato && <span className="ml-1 text-[9px] text-blue-600">✎</span>}
                            </span>
                            <strong className="text-slate-800 ml-2">€{prezzoFinale.toFixed(2)}</strong>
                          </div>
                        );
                      })}
                      {serviziAggiunti.map((sa) => {
                        const prezzoFinale = prezziModificati[sa.idx] !== undefined && prezziModificati[sa.idx] !== ''
                          ? parseFloat(prezziModificati[sa.idx]) : (sa.prezzo || 0);
                        return (
                          <div key={`agg-${sa.idx}`} className="flex justify-between text-xs py-0.5">
                            <span className="text-slate-700 truncate flex-1">
                              {sa.nome} <span className="text-[9px] text-emerald-600">+</span>
                            </span>
                            <strong className="text-slate-800 ml-2">€{prezzoFinale.toFixed(2)}</strong>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Prodotti */}
                  {prodottiSelezionati.length > 0 && (
                    <div className="border-t border-dashed border-slate-300 pt-2">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Prodotti</p>
                      {prodottiSelezionati.map((p) => (
                        <div key={p.id} className="flex justify-between text-xs py-0.5">
                          <span className="text-slate-700 truncate flex-1">
                            {p.nome} <span className="text-[9px] text-slate-500">x{p.quantita}</span>
                          </span>
                          <strong className="text-slate-800 ml-2">€{(p.prezzo_vendita * p.quantita).toFixed(2)}</strong>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Servizi rimossi (visualizzati per trasparenza) */}
                  {clienteSelezionato.servizi?.some((_, idx) => serviziRimossi.has(idx)) && (
                    <div className="border-t border-dashed border-red-200 pt-2 bg-red-50 -mx-4 px-4 py-1">
                      <p className="text-[10px] text-red-500 uppercase tracking-wider mb-1">Rimossi dallo scontrino</p>
                      {clienteSelezionato.servizi?.map((s, idx) => serviziRimossi.has(idx) && (
                        <div key={`rim-${idx}`} className="flex justify-between text-xs py-0.5 line-through text-red-400">
                          <span className="truncate flex-1">{s.nome}</span>
                          <span className="ml-2">€{parseFloat(s.prezzo || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Subtotale + sconto */}
                  <div className="border-t-2 border-dashed border-slate-300 pt-2 space-y-0.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Subtotale</span>
                      <span className="text-slate-700">€{subtotale.toFixed(2)}</span>
                    </div>
                    {scontoApplicato > 0.005 && (
                      <div className="flex justify-between text-emerald-700">
                        <span>Sconto {scontoTipo === 'percentuale' ? `(${scontoValore}%)` : ''}</span>
                        <span>−€{scontoApplicato.toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  {/* TOTALE */}
                  <div className="border-t-2 border-slate-800 pt-2 mt-2 bg-slate-50 -mx-4 px-4 py-3">
                    <div className="flex justify-between items-center">
                      <span className="text-base font-bold text-slate-800">TOTALE</span>
                      <span className="text-2xl font-black text-emerald-600" data-testid="anteprima-totale">
                        €{totale.toFixed(2)}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 text-right">Pagamento: {metodoLabel}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 p-3 bg-slate-50 border-t rounded-b-lg">
                  <Button
                    variant="outline"
                    onClick={() => { setShowAnteprimaScontrino(false); setMetodoPendente(null); }}
                    className="flex-1"
                    data-testid="btn-annulla-anteprima"
                  >
                    ← Modifica
                  </Button>
                  <Button
                    onClick={async () => {
                      setShowAnteprimaScontrino(false);
                      await handlePagamentoClick(metodoPendente);
                      setMetodoPendente(null);
                    }}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                    data-testid="btn-conferma-anteprima"
                  >
                    ✓ Conferma & Paga
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
